import { useRef } from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  DropResult,
  Droppable,
  DroppableProvided,
  ResponderProvided,
} from 'react-beautiful-dnd';
import { reorder } from './utils';
import {
  Table,
  TableHead,
  TableCell,
  TableBody,
  Typography,
  TableRow,
} from '@mui/material';
import { Cell } from '../TableShared/Cell';
import { colors } from '../colors';
import { faGripDotsVertical } from '@fortawesome/pro-regular-svg-icons';
import { neutral } from '../../themes/colors';
import {
  StandardTableProps,
  ColumnSchema,
  Row,
} from '../StandardTable/StandardTable';
import { F1 } from '../../helpers/types';

export type { Row as RowType } from '../StandardTable/StandardTable';

type RTableRowProps<T extends string | number> = {
  provided: DraggableProvided;
  snapshot?: DraggableStateSnapshot;
  schema: Array<
    Pick<ColumnSchema, 'label' | 'width' | 'cellWidth' | 'justify'>
  >;
  index: number;
} & Row<T>;

const dragCSSWidth: Pick<React.CSSProperties, 'width'> = { width: '46px' };
const toCSSWidth = (value: ColumnSchema['width']) => ({
  width: {
    sm: value === 'stretchable' ? 'auto' : '60px',
    md: value === 'stretchable' ? 'auto' : value + 'px',
  },
  minWidth: {
    sm: value === 'stretchable' ? '120px' : '60px',
    md: value === 'stretchable' ? '240px' : '60px',
  },
});

const DragTableRow = <T extends string | number>({
  provided,
  ...p
}: RTableRowProps<T>) => {
  const isDragging = p.snapshot ? p.snapshot.isDragging : false;
  return (
    <TableRow
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      role="row"
      sx={{
        display: isDragging ? 'table' : 'inline-table',
        width: '100%',
        backgroundColor: neutral['50'],
        marginBottom: '1em',
        borderRadius: '10px',
        boxShadow: `0px 0px 1px ${neutral['200']}, 0px 1px 2px rgba(29, 41, 57, 0.08)`,
      }}
    >
      {p.cells.map((cell, i) => {
        const schemaWidth =
          i < p.schema.length
            ? p.schema[i].cellWidth ?? p.schema[i].width
            : 'stretchable';
        return (
          <TableCell
            key={p.id + cell.type + i}
            sx={{
              ...toCSSWidth(schemaWidth),
              textAlign: p.schema[i]?.justify || 'left',
            }}
          >
            <Cell {...cell} index={p.index} />
          </TableCell>
        );
      })}
      <TableCell sx={dragCSSWidth}>
        <Cell type="IconText" value={{ icon: faGripDotsVertical }} />
      </TableCell>
    </TableRow>
  );
};

export type DragTableProps<T extends string | number> =
  StandardTableProps<T> & { onChange: F1<Array<Row<T>>> };

type HandleDragEndParams<T extends string | number> = {
  rows: DragTableProps<T>['rows'];
  setRows: (v: DragTableProps<T>['rows']) => void;
};

const handleDragEnd =
  <T extends string | number>({ rows, setRows }: HandleDragEndParams<T>) =>
  ({ source, destination }: DropResult, _provided: ResponderProvided) => {
    if (!destination) return null;
    if (source.droppableId !== destination.droppableId) return;
    const nextRows = reorder({
      src: rows,
      start: source.index,
      end: destination.index,
    });
    setRows(nextRows);
  };

export const DragTable = <T extends string | number = string>(
  p: DragTableProps<T>,
) => {
  const tableRef = useRef<HTMLElement | null>(null);

  const onDragEnd = handleDragEnd({ rows: p.rows, setRows: p.onChange });
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Table
        sx={{ userSelect: 'none', ...(p.sx || {}) }}
        className={p.className}
      >
        {p.noHeader ? null : (
          <TableHead sx={{ backgroundColor: 'transparent' }}>
            <TableRow sx={{ display: 'inline-table', width: '100%' }}>
              {p.schema.map((column, i) => (
                <TableCell
                  key={column.label || 'column' + i}
                  sx={{
                    ...toCSSWidth(column.width),
                    textAlign: column.justify || 'left',
                  }}
                  colSpan={column.colSpan}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      color: colors.text800,
                    }}
                  >
                    {column.label}
                  </Typography>
                </TableCell>
              ))}
              <TableCell sx={dragCSSWidth} />
            </TableRow>
          </TableHead>
        )}
        <Droppable droppableId="table">
          {(droppableProvided: DroppableProvided) => (
            <TableBody
              ref={(ref: HTMLElement | null) => {
                tableRef.current = ref;
                droppableProvided.innerRef(ref);
              }}
              {...droppableProvided.droppableProps}
            >
              {p.rows.map((row, index) => (
                <Draggable draggableId={row.id + ''} index={index} key={row.id}>
                  {(
                    provided: DraggableProvided,
                    snapshot: DraggableStateSnapshot,
                  ) => (
                    <DragTableRow
                      schema={p.schema}
                      provided={provided}
                      snapshot={snapshot}
                      index={index}
                      {...row}
                    />
                  )}
                </Draggable>
              ))}
              {droppableProvided.placeholder}
            </TableBody>
          )}
        </Droppable>
      </Table>
    </DragDropContext>
  );
};
